import { ClientPublicService } from '../../../../app/api/ClientPublicService';
import { inputDateToServerFormat, serverDateToInputFormat } from '../../../../app/utils/dateTimeHelpers';

import type {
  IFormValuesEmploymentDetail,
  IFormValuesPreviousAddress,
  IFormValuesInsurancePeriod,
  IFormValuesDependant,
  IFormValuesInsolvency,
  IFormValuesPersonalInfo,
  IFormValuesVoluntarySelfIdentification,
} from './types';

const getEmploymentDetailsFormItem = (item?: ClientPublicService.AppFormPersonalInfoEmploymentDetailDto) =>
  ({
    id: item?.id || '',
    employerName: item?.employerName || '',
    employerAddress: item?.employerAddress || '',
    employerStreetNumber: item?.employerStreetNumber || '',
    employerStreetName: item?.employerStreetName || '',
    employerUnit: item?.employerUnit || '',
    employerCity: item?.employerCity || '',
    employerPostalCode: item?.employerPostalCode || '',
    employerProvinceId: item?.employerProvinceId || '',
    employerCountryId: item?.employerCountryId || '',
    occupationName: item?.occupationName || '',
    industryId: item?.industryId || '',
    employmentStatusId: item?.employmentStatusId || '',
    startDate: serverDateToInputFormat(item?.startDate) || null,
    endDate: serverDateToInputFormat(item?.endDate) || null,
    isCurrentEmployer: item?.isCurrentEmployer || false,
  } as IFormValuesEmploymentDetail);

const convertEmploymentDetailFormItemToDto = (item?: IFormValuesEmploymentDetail) =>
  ({
    id: item?.id || undefined,
    employerName: item?.employerName || undefined,
    employerAddress: item?.employerAddress || undefined,
    employerStreetNumber: item?.employerStreetNumber || undefined,
    employerStreetName: item?.employerStreetName || undefined,
    employerUnit: item?.employerUnit || undefined,
    employerCity: item?.employerCity || undefined,
    employerPostalCode: item?.employerPostalCode || undefined,
    employerProvinceId: item?.employerProvinceId || undefined,
    employerCountryId: item?.employerCountryId || undefined,
    occupationName: item?.occupationName || undefined,
    industryId: item?.industryId || undefined,
    employmentStatusId: item?.employmentStatusId || undefined,
    startDate: inputDateToServerFormat(item?.startDate) || undefined,
    endDate: inputDateToServerFormat(item?.endDate) || undefined,
    isCurrentEmployer: item?.isCurrentEmployer || false,
  } as ClientPublicService.AppFormPersonalInfoEmploymentDetailDto);

const getPreviousAddressFormItem = (item?: ClientPublicService.AppFormPersonalInfoPreviousAddressDto) =>
  ({
    id: item?.id || '',
    address: item?.address || '',
    streetNumber: item?.streetNumber || '',
    streetName: item?.streetName || '',
    unit: item?.unit || '',
    city: item?.city || '',
    postalCode: item?.postalCode || '',
    provinceId: item?.provinceId || '',
    countryId: item?.countryId || '',
  } as IFormValuesPreviousAddress);

const convertPreviousAddressFormItemToDto = (item?: IFormValuesPreviousAddress) =>
  ({
    id: item?.id || undefined,
    address: item?.address || undefined,
    streetNumber: item?.streetNumber || undefined,
    streetName: item?.streetName || undefined,
    unit: item?.unit || undefined,
    city: item?.city || undefined,
    postalCode: item?.postalCode || undefined,
    provinceId: item?.provinceId || undefined,
    countryId: item?.countryId || undefined,
  } as ClientPublicService.AppFormPersonalInfoPreviousAddressDto);

const getInsurancePeriodFormItem = (item?: ClientPublicService.AppFormPersonalInfoEmploymentInsurancePeriodDto) =>
  ({
    id: item?.id || '',
    startDate: serverDateToInputFormat(item?.startDate) || null,
    endDate: serverDateToInputFormat(item?.endDate) || null,
    isCurrentPeriod: item?.isCurrentPeriod || false,
  } as IFormValuesInsurancePeriod);

const convertInsurancePeriodFormItemToDto = (item?: IFormValuesInsurancePeriod) =>
  ({
    id: item?.id || undefined,
    startDate: inputDateToServerFormat(item?.startDate) || undefined,
    endDate: inputDateToServerFormat(item?.endDate) || undefined,
    isCurrentPeriod: item?.isCurrentPeriod,
  } as ClientPublicService.AppFormPersonalInfoEmploymentInsurancePeriodDto);

const getDependantFormItem = (item?: ClientPublicService.AppFormPersonalInfoDependantDto) =>
  ({
    id: item?.id || '',
    firstName: item?.firstName || '',
    lastName: item?.lastName || '',
    relationshipTypeId: item?.relationshipTypeId || '',
    birthDate: serverDateToInputFormat(item?.birthDate) || null,
    isSameAsApplicantsContactInfo: item?.isSameAsApplicantsContactInfo || false,
    address: item?.address || '',
    streetNumber: item?.streetNumber || '',
    streetName: item?.streetName || '',
    unit: item?.unit || '',
    city: item?.city || '',
    postalCode: item?.postalCode || '',
    provinceId: item?.provinceId || '',
    countryId: item?.countryId || '',
  } as IFormValuesDependant);

const convertDependantFormItemToDto = (item?: IFormValuesDependant) =>
  ({
    id: item?.id || undefined,
    firstName: item?.firstName || undefined,
    lastName: item?.lastName || undefined,
    relationshipTypeId: item?.relationshipTypeId || undefined,
    birthDate: inputDateToServerFormat(item?.birthDate) || undefined,
    isSameAsApplicantsContactInfo: item?.isSameAsApplicantsContactInfo || false,
    address: item?.address || undefined,
    streetNumber: item?.streetNumber || undefined,
    streetName: item?.streetName || undefined,
    unit: item?.unit || undefined,
    city: item?.city || undefined,
    postalCode: item?.postalCode || undefined,
    provinceId: item?.provinceId || undefined,
    countryId: item?.countryId || undefined,
  } as ClientPublicService.AppFormPersonalInfoDependantDto);

const getInsolvencyFormItem = (item?: ClientPublicService.AppFormPersonalInfoPreviousInsolvencyDto) =>
  ({
    id: item?.id || '',
    insolvencyTypeId: item?.insolvencyTypeId || '',
    applicantName: item?.applicantName || '',
    trusteeName: item?.trusteeName || '',
    estateNumber: item?.estateNumber || '',
    bankruptcyDischargeTypeId: item?.bankruptcyDischargeTypeId || '',
    bankruptcyDate: serverDateToInputFormat(item?.bankruptcyDate) || null,
    dischargeDate: serverDateToInputFormat(item?.dischargeDate) || null,
    proposalDate: serverDateToInputFormat(item?.proposalDate) || null,
    proposalDischargeDate: serverDateToInputFormat(item?.proposalDischargeDate) || null,
    proposalResultId: item?.proposalResultId || '',
    filingLocation: item?.filingLocation || '',
    address: item?.address || '',
    streetNumber: item?.streetNumber || '',
    streetName: item?.streetName || '',
    unit: item?.unit || '',
    city: item?.city || '',
    postalCode: item?.postalCode || '',
    provinceId: item?.provinceId || '',
    countryId: item?.countryId || '',
  } as IFormValuesInsolvency);

const getVoluntarySelfIdentificationItem = (
  item?: ClientPublicService.AppFormPersonalInfoSelfIdentificationDto,
  isForSpouse?: boolean
) =>
  ({
    id: item?.id || '',
    appFormPersonalInfoId: item?.appFormPersonalInfoId || '',
    isForSpouse: item?.isForSpouse || isForSpouse || false,
    genderId: item?.genderId || '',
    educationLevelId: item?.educationLevelId || '',
    isAboriginal: item?.isAboriginal,
    isNorthAmericanIndianFirstNation: item?.isNorthAmericanIndianFirstNation || false,
    isInuit: item?.isInuit || false,
    isMétis: item?.isMétis || false,
    hasDisability: item?.hasDisability,
    hasDisabilityCoordinationOrDexterity: item?.hasDisabilityCoordinationOrDexterity || false,
    hasDisabilityMobility: item?.hasDisabilityMobility || false,
    hasDisabilityBlindOrVisualImpairment: item?.hasDisabilityBlindOrVisualImpairment || false,
    hasDisabilityDeafOrHardOfHearing: item?.hasDisabilityDeafOrHardOfHearing || false,
    hasDisabilitySpeechImpairment: item?.hasDisabilitySpeechImpairment || false,
    hasDisabilityOther: item?.hasDisabilityOther || false,
    disabilityOtherDescription: item?.disabilityOtherDescription || '',
    isMemberOfMinorityGroup: item?.isMemberOfMinorityGroup,
    isBlack: item?.isBlack || false,
    isChinese: item?.isChinese || false,
    isFilipino: item?.isFilipino || false,
    isJapanese: item?.isJapanese || false,
    isKorean: item?.isKorean || false,
    isSouthAsianEastIndian: item?.isSouthAsianEastIndian || false,
    isSoutheastAsian: item?.isSoutheastAsian || false,
    isNonWhiteWestAsianNorthAfricanOrArab: item?.isNonWhiteWestAsianNorthAfricanOrArab || false,
    isNonWhiteLatinAmerican: item?.isNonWhiteLatinAmerican || false,
    isPersonOfMixedOrigin: item?.isPersonOfMixedOrigin || false,
    isOtherVisibleMinorityGroup: item?.isOtherVisibleMinorityGroup || false,
    otherVisibleMinorityGroupDescription: item?.otherVisibleMinorityGroupDescription || '',
    consentDate: item?.consentDate || null,
    consentCity: item?.consentCity || '',
    consentProvinceId: item?.consentProvinceId || '',
  } as IFormValuesVoluntarySelfIdentification);

const convertInsolvencyFormItemToDto = (item?: IFormValuesInsolvency) =>
  ({
    id: item?.id || undefined,
    insolvencyTypeId: item?.insolvencyTypeId || undefined,
    applicantName: item?.applicantName || undefined,
    trusteeName: item?.trusteeName || undefined,
    estateNumber: item?.estateNumber || undefined,
    bankruptcyDischargeTypeId: item?.bankruptcyDischargeTypeId || undefined,
    bankruptcyDate: inputDateToServerFormat(item?.bankruptcyDate) || undefined,
    dischargeDate: inputDateToServerFormat(item?.dischargeDate) || undefined,
    proposalDate: inputDateToServerFormat(item?.proposalDate) || undefined,
    proposalDischargeDate: inputDateToServerFormat(item?.proposalDischargeDate) || undefined,
    proposalResultId: item?.proposalResultId || undefined,
    filingLocation: item?.filingLocation || undefined,
    address: item?.address || undefined,
    streetNumber: item?.streetNumber || undefined,
    streetName: item?.streetName || undefined,
    unit: item?.unit || undefined,
    city: item?.city || undefined,
    postalCode: item?.postalCode || undefined,
    provinceId: item?.provinceId || undefined,
    countryId: item?.countryId || undefined,
  } as ClientPublicService.AppFormPersonalInfoPreviousInsolvencyDto);

const convertVoluntarySelfIdentificationItemToDto = (item?: IFormValuesVoluntarySelfIdentification) =>
  ({
    id: item?.id || undefined,
    appFormPersonalInfoId: item?.appFormPersonalInfoId || undefined,
    isForSpouse: item?.isForSpouse || undefined,
    genderId: item?.genderId || undefined,
    educationLevelId: item?.educationLevelId || undefined,
    isAboriginal: item?.isAboriginal,
    isNorthAmericanIndianFirstNation: item?.isNorthAmericanIndianFirstNation || undefined,
    isInuit: item?.isInuit || undefined,
    isMétis: item?.isMétis || undefined,
    hasDisability: item?.hasDisability,
    hasDisabilityCoordinationOrDexterity: item?.hasDisabilityCoordinationOrDexterity || undefined,
    hasDisabilityMobility: item?.hasDisabilityMobility || undefined,
    hasDisabilityBlindOrVisualImpairment: item?.hasDisabilityBlindOrVisualImpairment || undefined,
    hasDisabilityDeafOrHardOfHearing: item?.hasDisabilityDeafOrHardOfHearing || undefined,
    hasDisabilitySpeechImpairment: item?.hasDisabilitySpeechImpairment || undefined,
    hasDisabilityOther: item?.hasDisabilityOther || undefined,
    disabilityOtherDescription: item?.disabilityOtherDescription || undefined,
    isMemberOfMinorityGroup: item?.isMemberOfMinorityGroup,
    isBlack: item?.isBlack || undefined,
    isChinese: item?.isChinese || undefined,
    isFilipino: item?.isFilipino || undefined,
    isJapanese: item?.isJapanese || undefined,
    isKorean: item?.isKorean || undefined,
    isSouthAsianEastIndian: item?.isSouthAsianEastIndian || undefined,
    isSoutheastAsian: item?.isSoutheastAsian || undefined,
    isNonWhiteWestAsianNorthAfricanOrArab: item?.isNonWhiteWestAsianNorthAfricanOrArab || undefined,
    isNonWhiteLatinAmerican: item?.isNonWhiteLatinAmerican || undefined,
    isPersonOfMixedOrigin: item?.isPersonOfMixedOrigin || undefined,
    isOtherVisibleMinorityGroup: item?.isOtherVisibleMinorityGroup || undefined,
    otherVisibleMinorityGroupDescription: item?.otherVisibleMinorityGroupDescription || undefined,
    consentDate: item?.consentDate || undefined,
    consentCity: item?.consentCity || undefined,
    consentProvinceId: item?.consentProvinceId || undefined,
  } as ClientPublicService.AppFormPersonalInfoSelfIdentificationDto);

const getInitialPersonalInfoValues = (
  data?: ClientPublicService.IAppFormPersonalInfoDto,
  documents?: ClientPublicService.DocumentDto[]
) =>
  ({
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    middleName: data?.middleName || '',
    akaFirstName: data?.akaFirstName || '',
    akaMiddleName: data?.akaMiddleName || '',
    akaLastName: data?.akaLastName || '',
    birthDate: serverDateToInputFormat(data?.birthDate) || null,
    sin: data?.sin || '',
    maritalStatusId: data?.maritalStatusId || '',
    marriageDate: serverDateToInputFormat(data?.marriageDate) || null,
    educationLevelId: data?.educationLevelId || '',
    spouseFirstName: data?.spouseFirstName || '',
    spouseMiddleName: data?.spouseMiddleName || '',
    spouseLastName: data?.spouseLastName || '',
    spouseAkaFirstName: data?.spouseAkaFirstName || '',
    spouseAkaMiddleName: data?.spouseAkaMiddleName || '',
    spouseAkaLastName: data?.spouseAkaLastName || '',
    spouseBirthDate: serverDateToInputFormat(data?.spouseBirthDate) || null,
    spouseSIN: data?.spouseSIN || '',
    spouseEducationLevelId: data?.spouseEducationLevelId || '',
    isJointFile: data?.isJointFile || false,
    additionalNotes: data?.additionalNotes || '',
    address: data?.address || '',
    streetNumber: data?.streetNumber || '',
    streetName: data?.streetName || '',
    unit: data?.unit || '',
    city: data?.city || '',
    provinceId: data?.provinceId || '',
    postalCode: data?.postalCode || '',
    countryId: data?.countryId || '',
    cityTypeId: data?.cityTypeId || '',
    residenceDate: serverDateToInputFormat(data?.residenceDate) || null,
    previousAddresses: data?.previousAddresses?.map((item) => getPreviousAddressFormItem(item)) || [],
    mainPhoneTypeId: data?.mainPhoneTypeId || '',
    mainPhoneNumber: data?.mainPhoneNumber || '',
    emailAddress: data?.emailAddress || '',
    spouseAddress: data?.spouseAddress || '',
    spouseUnit: data?.spouseUnit || '',
    spouseCity: data?.spouseCity || '',
    spouseStreetNumber: data?.spouseStreetNumber || '',
    spouseStreetName: data?.spouseStreetName || '',
    spouseProvinceId: data?.spouseProvinceId || '',
    spousePostalCode: data?.spousePostalCode || '',
    spouseCountryId: data?.spouseCountryId || '',
    spouseResidenceDate: serverDateToInputFormat(data?.spouseResidenceDate),
    spouseMainPhoneTypeId: data?.spouseMainPhoneTypeId || '',
    spouseMainPhoneNumber: data?.spouseMainPhoneNumber || '',
    spouseEmailAddress: data?.spouseEmailAddress || '',
    isSpouseAddressSame: data?.isSpouseAddressSame || false,
    isEmployedPastTwoYears: data?.isEmployedPastTwoYears || false,
    employmentDetails: data?.employmentDetails?.map((item) => getEmploymentDetailsFormItem(item)) || [],
    isReceivedEmploymentInsurancePastTwoYears: data?.isReceivedEmploymentInsurancePastTwoYears || false,
    employmentInsurancePeriods: data?.employmentInsurancePeriods?.map((item) => getInsurancePeriodFormItem(item)) || [],
    isSpouseEmployedPastTwoYears: data?.isSpouseEmployedPastTwoYears || false,
    spouseEmploymentDetails: data?.spouseEmploymentDetails?.map((item) => getEmploymentDetailsFormItem(item)) || [],
    isSpouseReceivedEmploymentInsurancePastTwoYears: data?.isSpouseReceivedEmploymentInsurancePastTwoYears || false,
    spouseEmploymentInsurancePeriods:
      data?.spouseEmploymentInsurancePeriods?.map((item) => getInsurancePeriodFormItem(item)) || [],
    hasFDReasonLossOfIncome: data?.hasFDReasonLossOfIncome || false,
    hasFDReasonMedicalReasons: data?.hasFDReasonMedicalReasons || false,
    hasFDReasonBusinessFailure: data?.hasFDReasonBusinessFailure || false,
    hasFDReasonGambling: data?.hasFDReasonGambling || false,
    hasFDReasonDrugAlcoholAddicition: data?.hasFDReasonDrugAlcoholAddicition || false,
    hasFDReasonMaritalSeparationOrRelationshipBreakdown:
      data?.hasFDReasonMaritalSeparationOrRelationshipBreakdown || false,
    hasFDReasonTaxLiabilities: data?.hasFDReasonTaxLiabilities || false,
    hasFDReasonFinancialSupportOfOthers: data?.hasFDReasonFinancialSupportOfOthers || false,
    hasFDReasonStudentDebt: data?.hasFDReasonStudentDebt || false,
    hasFDReasonLitigationIssues: data?.hasFDReasonLitigationIssues || false,
    hasFDReasonMovingOrRelocationExpenses: data?.hasFDReasonMovingOrRelocationExpenses || false,
    hasFDReasonFailedProposal: data?.hasFDReasonFailedProposal || false,
    hasFDReasonFinancialMismanagement: data?.hasFDReasonFinancialMismanagement || false,
    hasFDReasonOther: data?.hasFDReasonOther || false,
    specifiedFDReason: data?.specifiedFDReason || '',
    specifyFDReasonLegalMatters: data?.specifyFDReasonLegalMatters || '',
    reasonForNeedingFinancialHelp: data?.reasonForNeedingFinancialHelp || '',

    spouseHasFDReasonLossOfIncome: data?.spouseHasFDReasonLossOfIncome || false,
    spouseHasFDReasonMedicalReasons: data?.spouseHasFDReasonMedicalReasons || false,
    spouseHasFDReasonBusinessFailure: data?.spouseHasFDReasonBusinessFailure || false,
    spouseHasFDReasonGambling: data?.spouseHasFDReasonGambling || false,
    spouseHasFDReasonDrugAlcoholAddicition: data?.spouseHasFDReasonDrugAlcoholAddicition || false,
    spouseHasFDReasonMaritalSeparationOrRelationshipBreakdown:
      data?.spouseHasFDReasonMaritalSeparationOrRelationshipBreakdown || false,
    spouseHasFDReasonTaxLiabilities: data?.spouseHasFDReasonTaxLiabilities || false,
    spouseHasFDReasonFinancialSupportOfOthers: data?.spouseHasFDReasonFinancialSupportOfOthers || false,
    spouseHasFDReasonStudentDebt: data?.spouseHasFDReasonStudentDebt || false,
    spouseHasFDReasonLitigationIssues: data?.spouseHasFDReasonLitigationIssues || false,
    spouseHasFDReasonMovingOrRelocationExpenses: data?.spouseHasFDReasonMovingOrRelocationExpenses || false,
    spouseHasFDReasonFailedProposal: data?.spouseHasFDReasonFailedProposal || false,
    spouseHasFDReasonFinancialMismanagement: data?.spouseHasFDReasonFinancialMismanagement || false,
    spouseHasFDReasonOther: data?.spouseHasFDReasonOther || false,
    spouseSpecifiedFDReason: data?.spouseSpecifiedFDReason || '',
    spouseSpecifyFDReasonLegalMatters: data?.spouseSpecifyFDReasonLegalMatters || '',
    spouseReasonForNeedingFinancialHelp: data?.spouseReasonForNeedingFinancialHelp || '',

    numberOfPersonsInHousehold:
      typeof data?.numberOfPersonsInHousehold === 'number' ? data.numberOfPersonsInHousehold : undefined,
    hasDependants: data?.hasDependants || false,
    dependants: data?.dependants?.map((item) => getDependantFormItem(item)) || [],
    hasPreviousInsolvency: data?.hasPreviousInsolvency || false,
    previousInsolvencies: data?.previousInsolvencies?.map((item) => getInsolvencyFormItem(item)) || [],
    hasSpousePreviousInsolvency: data?.hasSpousePreviousInsolvency || false,
    spousePreviousInsolvencies: data?.spousePreviousInsolvencies?.map((item) => getInsolvencyFormItem(item)) || [],
    documentIds: data?.documentIds || [],
    documents,

    isSelfIdentificationUpdatedByClient: data?.isSelfIdentificationUpdatedByClient,
    consentToSelfIdentification: data?.consentToSelfIdentification,
    consentToSelfIdentificationSpouse: data?.consentToSelfIdentificationSpouse,
    selfIdentifications:
      data?.selfIdentifications?.map((item) => convertVoluntarySelfIdentificationItemToDto(item)) || [],
    selfIdentificationsSpouse:
      data?.selfIdentificationsSpouse?.map((item) => convertVoluntarySelfIdentificationItemToDto(item)) || [],
  } as IFormValuesPersonalInfo);

const convertFormValuesToAppFormPersonalInfoDto = (formValues?: IFormValuesPersonalInfo) => ({
  ...formValues,
  firstName: formValues?.firstName || undefined,
  lastName: formValues?.lastName || undefined,
  middleName: formValues?.middleName || undefined,
  akaFirstName: formValues?.akaFirstName || undefined,
  akaMiddleName: formValues?.akaMiddleName || undefined,
  akaLastName: formValues?.akaLastName || undefined,
  birthDate: inputDateToServerFormat(formValues?.birthDate) || undefined,
  sin: formValues?.sin || undefined,
  maritalStatusId: formValues?.maritalStatusId || undefined,
  marriageDate: inputDateToServerFormat(formValues?.marriageDate) || undefined,
  educationLevelId: formValues?.educationLevelId || undefined,
  spouseFirstName: formValues?.spouseFirstName || undefined,
  spouseMiddleName: formValues?.spouseMiddleName || undefined,
  spouseLastName: formValues?.spouseLastName || undefined,
  spouseAkaFirstName: formValues?.spouseAkaFirstName || undefined,
  spouseAkaMiddleName: formValues?.spouseAkaMiddleName || undefined,
  spouseAkaLastName: formValues?.spouseAkaLastName || undefined,
  spouseBirthDate: inputDateToServerFormat(formValues?.spouseBirthDate) || undefined,
  spouseSIN: formValues?.spouseSIN || undefined,
  spouseEducationLevelId: formValues?.spouseEducationLevelId || undefined,
  isJointFile: formValues?.isJointFile || false,
  additionalNotes: formValues?.additionalNotes || undefined,
  address: formValues?.address || undefined,
  streetNumber: formValues?.streetNumber || undefined,
  streetName: formValues?.streetName || undefined,
  unit: formValues?.unit || undefined,
  city: formValues?.city || undefined,
  provinceId: formValues?.provinceId || undefined,
  postalCode: formValues?.postalCode || undefined,
  countryId: formValues?.countryId || undefined,
  cityTypeId: formValues?.cityTypeId || undefined,
  residenceDate: inputDateToServerFormat(formValues?.residenceDate) || undefined,
  previousAddresses: formValues?.previousAddresses?.map((item) => convertPreviousAddressFormItemToDto(item)) || [],
  mainPhoneTypeId: formValues?.mainPhoneTypeId || undefined,
  mainPhoneNumber: formValues?.mainPhoneNumber || undefined,
  emailAddress: formValues?.emailAddress || undefined,
  spouseAddress: formValues?.spouseAddress || undefined,
  spouseUnit: formValues?.spouseUnit || undefined,
  spouseCity: formValues?.spouseCity || undefined,
  spouseStreetNumber: formValues?.spouseStreetNumber || undefined,
  spouseStreetName: formValues?.spouseStreetName || undefined,
  spouseProvinceId: formValues?.spouseProvinceId || undefined,
  spousePostalCode: formValues?.spousePostalCode || undefined,
  spouseCountryId: formValues?.spouseCountryId || undefined,
  spouseResidenceDate: inputDateToServerFormat(formValues?.spouseResidenceDate) || undefined,
  spouseMainPhoneTypeId: formValues?.spouseMainPhoneTypeId || undefined,
  spouseMainPhoneNumber: formValues?.spouseMainPhoneNumber || undefined,
  spouseEmailAddress: formValues?.spouseEmailAddress || undefined,
  employmentDetails: formValues?.employmentDetails?.map((item) => convertEmploymentDetailFormItemToDto(item)) || [],
  employmentInsurancePeriods:
    formValues?.employmentInsurancePeriods?.map((item) => convertInsurancePeriodFormItemToDto(item)) || [],
  spouseEmploymentDetails:
    formValues?.spouseEmploymentDetails?.map((item) => convertEmploymentDetailFormItemToDto(item)) || [],
  spouseEmploymentInsurancePeriods:
    formValues?.spouseEmploymentInsurancePeriods?.map((item) => convertInsurancePeriodFormItemToDto(item)) || [],
  specifiedFDReason: formValues?.specifiedFDReason || undefined,
  specifyFDReasonLegalMatters: formValues?.specifyFDReasonLegalMatters || undefined,
  reasonForNeedingFinancialHelp: formValues?.reasonForNeedingFinancialHelp || undefined,

  spouseSpecifiedFDReason: formValues?.spouseSpecifiedFDReason || undefined,
  spouseSpecifyFDReasonLegalMatters: formValues?.spouseSpecifyFDReasonLegalMatters || undefined,
  spouseReasonForNeedingFinancialHelp: formValues?.spouseReasonForNeedingFinancialHelp || undefined,

  dependants: formValues?.dependants?.map((item) => convertDependantFormItemToDto(item)) || [],
  previousInsolvencies: formValues?.previousInsolvencies?.map((item) => convertInsolvencyFormItemToDto(item)) || [],
  spousePreviousInsolvencies:
    formValues?.spousePreviousInsolvencies?.map((item) => convertInsolvencyFormItemToDto(item)) || [],
  documentIds: formValues?.documentIds || [],

  isSelfIdentificationUpdatedByClient: formValues?.isSelfIdentificationUpdatedByClient,
  consentToSelfIdentification: formValues?.consentToSelfIdentification,
  consentToSelfIdentificationSpouse: formValues?.consentToSelfIdentificationSpouse,
  selfIdentifications: formValues?.consentToSelfIdentification
    ? formValues?.selfIdentifications?.map((item) => convertVoluntarySelfIdentificationItemToDto(item))
    : [],
  selfIdentificationsSpouse: formValues?.consentToSelfIdentificationSpouse
    ? formValues?.selfIdentificationsSpouse?.map((item) => convertVoluntarySelfIdentificationItemToDto(item))
    : [],
});

export {
  getInitialPersonalInfoValues,
  convertFormValuesToAppFormPersonalInfoDto,
  getPreviousAddressFormItem,
  getEmploymentDetailsFormItem,
  convertEmploymentDetailFormItemToDto,
  getInsurancePeriodFormItem,
  getDependantFormItem,
  getInsolvencyFormItem,
  getVoluntarySelfIdentificationItem,
};
